import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useOrgGraphStatsRelayQuery as UseOrgGraphStatsRelayQuery } from './__generated__/useOrgGraphStatsRelayQuery.graphql';
import type { SystemStatsGranularity } from './__generated__/useOrgGraphStatsRelayQuery.graphql';
import { IDashboardGraph } from '../../../../ts/interface/dashboardGraph.interface';
import { IDashboardStat } from '../../../../ts/interface/dashboardStats.interface';
import { toDashboardStat } from './toDashboardStat';

const OrgGraphStatsQuery = graphql`
  query useOrgGraphStatsRelayQuery(
    $organizationId: ID!
    $startDate: DateTime
    $endDate: DateTime
    $granularity: SystemStatsGranularity!
  ) {
    organizationStats(
      organizationId: $organizationId
      startDate: $startDate
      endDate: $endDate
      granularity: $granularity
    ) {
      activeRentalsCount
      rentalsCount
      usersCount
      availableHardwareCount
      rentalsByPeriod {
        count
        period
      }
      usersByPeriod {
        count
        period
      }
      averageDurationByPeriod {
        averageDuration
        period
      }
    }
  }
`;

export interface OrgGraphStatsQueryParams {
  organizationId: string;
  startDate?: string;
  endDate?: string;
  granularity?: string;
  enabled?: boolean;
}

function toGqlGranularity(gran?: string): SystemStatsGranularity {
  return gran === 'year' ? 'MONTH' : 'DAY';
}

function parsePeriod(period: string, gran?: string): number {
  const parts = period.split('-');
  if (gran === 'year') {
    return parseInt(parts[1], 10);
  }
  return parseInt(parts[2], 10);
}

function transformPeriodStats(
  items: ReadonlyArray<{ readonly count: number; readonly period: string }>,
  gran?: string
): any[] {
  return items.map((item) => {
    const num = parsePeriod(item.period, gran);
    if (gran === 'year') {
      return { count: item.count, month: num };
    }
    return { count: item.count, day: num };
  });
}

function buildDurations(
  rentalsByPeriod: ReadonlyArray<{
    readonly count: number;
    readonly period: string;
  }>,
  durationsByPeriod: ReadonlyArray<{
    readonly averageDuration: number;
    readonly period: string;
  }>
): { [key: string]: number } {
  const totalRentals = rentalsByPeriod.reduce((sum, item) => sum + item.count, 0);
  if (!totalRentals) return {};

  const durationByPeriod: Record<string, number> = {};
  durationsByPeriod.forEach((item) => {
    durationByPeriod[item.period] = item.averageDuration;
  });

  const weightedTotalSeconds = rentalsByPeriod.reduce((sum, item) => {
    const avgSec = durationByPeriod[item.period] || 0;
    return sum + avgSec * item.count;
  }, 0);

  const totalDurationMinutes = weightedTotalSeconds / 60;

  return {
    total_rentals: totalRentals,
    total_duration: totalDurationMinutes,
  };
}

function transformDurationPeriods(
  items: ReadonlyArray<{
    readonly averageDuration: number;
    readonly period: string;
  }>,
  gran?: string
): any[] {
  return items.map((item) => {
    const num = parsePeriod(item.period, gran);
    const minutes = Math.round(item.averageDuration / 60);
    if (gran === 'year') {
      return { count: minutes, month: num };
    }
    return { count: minutes, day: num };
  });
}

export function useOrgGraphStatsRelay(params: OrgGraphStatsQueryParams) {
  const { organizationId, startDate, endDate, granularity, enabled = true } = params;
  const [data, setData] = useState<IDashboardGraph | null>(null);
  // Counter tiles, from the same response as the graph series. organizationStats
  // computes them on every call regardless of which fields are selected, so
  // reading them here costs nothing and saves the dashboard a second request.
  // They are point-in-time totals: the date arguments scope the series only, so
  // changing the range refetches identical counters rather than stale ones.
  const [stats, setStats] = useState<IDashboardStat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const gqlGranularity = toGqlGranularity(granularity);

  const fetchData = useCallback(() => {
    if (!enabled || !organizationId) return;

    setIsLoading(true);
    setError(null);

    const subscription = fetchQuery<UseOrgGraphStatsRelayQuery>(
      relayEnvironment,
      OrgGraphStatsQuery,
      { organizationId, startDate, endDate, granularity: gqlGranularity }
    ).subscribe({
      next: (response) => {
        const stats = response.organizationStats;
        const rentals = stats?.rentalsByPeriod || [];
        const durations = stats?.averageDurationByPeriod || [];
        const parsed: IDashboardGraph = {
          rentals: transformPeriodStats(rentals, granularity),
          users: transformPeriodStats(stats?.usersByPeriod || [], granularity),
          durations: buildDurations(rentals, durations),
          durationPeriods: transformDurationPeriods(durations, granularity),
        };
        setData(parsed);
        setStats(stats ? toDashboardStat(stats) : null);
        setIsLoading(false);
        setIsFetched(true);
      },
      error: (err: Error) => {
        setError(err);
        setIsLoading(false);
        setIsFetched(true);
      },
    });

    return () => subscription.unsubscribe();
  }, [organizationId, startDate, endDate, gqlGranularity, granularity, enabled]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  return { data, stats, isLoading, isFetched, error, refetch: fetchData };
}
